import React, { useState, useEffect, useRef } from 'react';
import CourseDetailsModal from './CourseDetailsModal';
import CourseStatusIndicator from './CourseStatusIndicator';
import GenderAvailabilityIndicator from './GenderAvailabilityIndicator';
import GuardianStudentSelectDialog from './GuardianStudentSelectDialog';
import { fetchCourses, checkAvailability, joinWaitlist, getWaitlistEntries, checkPromotionStatus } from '../services/courseService';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { RefreshCw } from 'lucide-react';

const CourseList = ({ onTabChange, onShowBookingWizard }) => {
  const { t } = useTranslation();
  const { user, groups } = useAuth();
  const isAdmin = groups?.includes('Admin');
  const isGuardian = groups?.includes('Guardian') || groups?.includes('Parent');
  const isStudent = groups?.includes('Student');
  
  // Debug logging
  /* console.log('CourseList auth state:', {
    userEmail: user?.username || user?.email,
    groups: groups,
    isGuardian: isGuardian
  });
   */
  // State management - moved from StudentDashboard
  const [courses, setCourses] = useState([]);
  const [courseAvailability, setCourseAvailability] = useState({});
  const [confirmedFullCourses, setConfirmedFullCourses] = useState(new Set()); // Track courses confirmed as full
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingCourse, setLoadingCourse] = useState(null);
  const [waitlistEntries, setWaitlistEntries] = useState([]);
  const [isRefreshingAvailability, setIsRefreshingAvailability] = useState(false);
  const [lastAvailabilityUpdate, setLastAvailabilityUpdate] = useState(null);
  const availabilityCacheRef = useRef(new Map());
  // TODO: PARKED - Frontend debouncing disabled due to UX concerns
  // const availabilityDebounceRef = useRef(new Map());

  // TODO: PARKED OPTIMIZATION - Debounced availability check
  // CONCERN: Could show stale data during high-traffic periods
  /*
  const checkAvailabilityDebounced = async (courseId) => {
    const DEBOUNCE_MS = 2000;
    const now = Date.now();
    const lastCheck = availabilityDebounceRef.current.get(courseId);
    if (lastCheck && (now - lastCheck) < DEBOUNCE_MS) {
      const cached = courseAvailability[courseId];
      if (cached) {
        return cached;
      }
    }
    availabilityDebounceRef.current.set(courseId, now);
    return await checkAvailability(courseId);
  };
  */

  // Modal state
  const [showCourseDetails, setShowCourseDetails] = useState(false);
  const [selectedCourseDetails, setSelectedCourseDetails] = useState(null);
  
  // Guardian-specific state
  const [showStudentSelectDialog, setShowStudentSelectDialog] = useState(false);
  const [selectedCourseForWaitlist, setSelectedCourseForWaitlist] = useState(null);

  // Load courses on mount
  useEffect(() => {
    loadCourses();
    if (isGuardian) {
      loadWaitlistEntries();
    }
  }, [isGuardian]);


  const loadWaitlistEntries = async () => {
    if (!isGuardian) return;
    
    try {
      const response = await getWaitlistEntries(user?.email);
      if (response?.entries) {
        setWaitlistEntries(response.entries);
        console.log('Guardian waitlist entries loaded:', response.entries);
        
        // Check for promoted students - only notify once per session
        const promotedEntries = response.entries.filter(e => e.status === 'promoted');
        if (promotedEntries.length > 0) {
          console.log('Found promoted students:', promotedEntries);

          // Get list of already-notified entries from sessionStorage
          const notifiedKey = 'waitlist_promotions_notified';
          const alreadyNotified = JSON.parse(sessionStorage.getItem(notifiedKey) || '[]');

          promotedEntries.forEach(entry => {
            const entryKey = `${entry.waitlistId || entry.queueId}_${entry.courseId}`;

            // Only show toast if we haven't notified about this entry in this session
            if (!alreadyNotified.includes(entryKey)) {
              toast.info(
                `${entry.studentName} has been promoted from the waitlist for ${entry.courseTitle || 'a course'}! Visit Waitlist tab to complete the booking.`,
                { autoClose: 10000 }
              );
              alreadyNotified.push(entryKey);
            }
          });

          // Save updated list back to sessionStorage
          sessionStorage.setItem(notifiedKey, JSON.stringify(alreadyNotified));
        }
      }
    } catch (error) {
      console.error('Failed to load waitlist entries:', error);
    }
  };

  // Data loading function - moved from StudentDashboard
  const loadCourses = async () => {
    try {
      setLoading(true);
      //console.log('Loading courses...');

      // Only show courses the student is eligible for (includeIneligible = false)
      const response = await fetchCourses(false);
      console.log('🔴 [API RESPONSE] Raw courses API response:', response);

      let coursesData = [];
      if (Array.isArray(response)) {
        coursesData = response;
      } else if (response && typeof response === 'object') {
        coursesData = response.courses || response.Items || response.items || [];
        if (response.data) {
          coursesData = Array.isArray(response.data) ? response.data :
            response.data.courses || response.data.Items || [];
        }
      }

      console.log('🔴 [API RESPONSE] Extracted courses data:', coursesData);
      console.log('🔴 [API RESPONSE] First course hideFromGuardian:', coursesData[0]?.hideFromGuardian);

      // Normalize course data structure
      let normalizedCourses = coursesData.map(course => {
        const normalized = {
          ...course,
          id: course.id || course.courseId,
          title: course.title || course.name || 'Unknown Course',
          price: course.price || course.basePrice || 0,
          currency: course.currency || 'EUR',
          availableSpots: course.availableSpots || 0,
          totalSpots: course.totalSpots || 0,
          isAdultOnly: course.isAdultOnly || course.adultOnly || false,
          minAge: course.minAge || course.minimumAge || 0,
          hideFromGuardian: course.hideFromGuardian || false,
          hideFromStudent: course.hideFromStudent || false
        };

         console.log('🔍 [DEBUG] Normalizing course:', {
          originalCourse: {
            id: course.id,
            courseId: course.courseId,
            title: course.title,
            availableSpots: course.availableSpots,
            totalSpots: course.totalSpots,
            hideFromGuardian: course.hideFromGuardian,
            hideFromStudent: course.hideFromStudent
          },
          normalizedCourse: {
            id: normalized.id,
            title: normalized.title,
            availableSpots: normalized.availableSpots,
            totalSpots: normalized.totalSpots,
            hideFromGuardian: normalized.hideFromGuardian,
            hideFromStudent: normalized.hideFromStudent
          }
        }); 

        return normalized;
      });

      // Filter out adult-only courses and courses marked as hideFromGuardian for guardians
      // IMPORTANT: Admins should NEVER have courses hidden, even if they're also Guardians
      if (isGuardian && !isAdmin) {
        const coursesBeforeFilter = normalizedCourses.length;

        // Debug: Log all courses before filtering
        console.log('🔴 [GUARDIAN FILTER] All courses before filtering:', normalizedCourses.map(c => ({
          id: c.id,
          title: c.title,
          hideFromGuardian: c.hideFromGuardian,
          hideFromGuardianType: typeof c.hideFromGuardian
        })));

        normalizedCourses = normalizedCourses.filter(course => {
          // Filter out courses explicitly hidden from guardians (handle both boolean and string values)
          const hideFromGuardian = String(course.hideFromGuardian).toLowerCase() === 'true' || course.hideFromGuardian === true;

          console.log(`🔴 [GUARDIAN FILTER] Course: ${course.title}`, {
            hideFromGuardian: course.hideFromGuardian,
            hideFromGuardianType: typeof course.hideFromGuardian,
            evaluatedToHide: hideFromGuardian
          });

          if (hideFromGuardian) {
            console.log(`🚫 Filtering out course hidden from guardians: ${course.title} (hideFromGuardian: ${course.hideFromGuardian})`);
            return false;
          }
          // Filter out courses marked as adult-only
          if (course.isAdultOnly) {
            console.log(`Filtering out adult-only course for guardian: ${course.title}`);
            return false;
          }
          // Also filter out courses with minAge >= 18 if they don't have the adult-only flag
          if (course.minAge >= 18) {
            console.log(`Filtering out 18+ course for guardian: ${course.title} (minAge: ${course.minAge})`);
            return false;
          }
          return true;
        });

        const coursesAfterFilter = normalizedCourses.length;
        if (coursesBeforeFilter !== coursesAfterFilter) {
          console.log(`Filtered out ${coursesBeforeFilter - coursesAfterFilter} courses from guardian view`);
        }
      }

      // Filter out courses marked as hideFromStudent for students
      // IMPORTANT: Admins should NEVER have courses hidden
      if (isStudent && !isAdmin) {
        const coursesBeforeFilter = normalizedCourses.length;

        // Debug: Log all courses before filtering
        console.log('🔴 [STUDENT FILTER] All courses before filtering:', normalizedCourses.map(c => ({
          id: c.id,
          title: c.title,
          hideFromStudent: c.hideFromStudent,
          hideFromStudentType: typeof c.hideFromStudent
        })));

        normalizedCourses = normalizedCourses.filter(course => {
          // Filter out courses explicitly hidden from students (handle both boolean and string values)
          const hideFromStudent = String(course.hideFromStudent).toLowerCase() === 'true' || course.hideFromStudent === true;

          console.log(`🔴 [STUDENT FILTER] Course: ${course.title}`, {
            hideFromStudent: course.hideFromStudent,
            hideFromStudentType: typeof course.hideFromStudent,
            evaluatedToHide: hideFromStudent
          });

          if (hideFromStudent) {
            console.log(`🚫 Filtering out course hidden from students: ${course.title} (hideFromStudent: ${course.hideFromStudent})`);
            return false;
          }
          return true;
        });

        const coursesAfterFilter = normalizedCourses.length;
        if (coursesBeforeFilter !== coursesAfterFilter) {
          console.log(`Filtered out ${coursesBeforeFilter - coursesAfterFilter} courses from student view`);
        }
      }

      setCourses(Array.isArray(normalizedCourses) ? normalizedCourses : []);
      //console.log('🔍 [DEBUG] All normalized courses:', normalizedCourses);
      
      // Load user's waitlist entries to check if already on waitlist
      let userWaitlistEntries = [];
      try {
        const waitlistResponse = await getWaitlistEntries();
        console.log('Loading user waitlist to check status:', waitlistResponse);
        if (Array.isArray(waitlistResponse)) {
          userWaitlistEntries = waitlistResponse;
        } else if (waitlistResponse?.waitlist) {
          userWaitlistEntries = waitlistResponse.waitlist;
        }
      } catch (err) {
        console.log('Could not load waitlist status:', err);
      }

      // Load availability for all courses
      if (typeof checkAvailability === 'function' && normalizedCourses.length > 0) {
        try {
          console.log('Loading availability for', normalizedCourses.length, 'courses');

          const availabilityPromises = normalizedCourses.map(async (course) => {
            try {
/*               //console.log('🔍 [DEBUG] CourseList checking availability for course:', {
                courseId: course.id,
                courseTitle: course.title,
                initialAvailableSpots: course.availableSpots,
                initialTotalSpots: course.totalSpots
              }); */
              
              const availability = await checkAvailability(course.id);
              
              const processedAvailability = {
                courseId: course.id,
                availableSpots: availability.availableSpots ?? course.availableSpots ?? 0,
                totalSpots: availability.totalSpots ?? course.totalSpots ?? 0,
                isFullyBooked: (availability.availableSpots ?? course.availableSpots ?? 0) <= 0
              };
              
           /*    //console.log('🔍 [DEBUG] CourseList processed availability for course', course.id, ':', {
                courseTitle: course.title,
                apiResponse: availability,
                processedResult: processedAvailability,
                calculation: {
                  'availability.availableSpots': availability.availableSpots,
                  'course.availableSpots': course.availableSpots,
                  'final availableSpots': processedAvailability.availableSpots,
                  'availability.totalSpots': availability.totalSpots,
                  'course.totalSpots': course.totalSpots,
                  'final totalSpots': processedAvailability.totalSpots
                }
              }); */
              
              return processedAvailability;
            } catch (err) {
              console.error(`❌ [DEBUG] Failed to check availability for course ${course.id}:`, err);
              const fallbackAvailability = {
                courseId: course.id,
                availableSpots: course.availableSpots ?? 0,
                totalSpots: course.totalSpots ?? 0,
                isFullyBooked: (course.availableSpots ?? 0) <= 0
              };
              
              //console.log('🔍 [DEBUG] CourseList using fallback availability for course', course.id, ':', fallbackAvailability);
              return fallbackAvailability;
            }
          });

          const availabilities = await Promise.all(availabilityPromises);
          const availabilityMap = {};
          const now = Date.now();
          availabilities.forEach(avail => {
            availabilityMap[avail.courseId] = avail;
            // Cache availability data
            availabilityCacheRef.current.set(avail.courseId, {
              data: avail,
              timestamp: now
            });
          });

          console.log('Availability map:', availabilityMap);
          setCourseAvailability(availabilityMap);
          setLastAvailabilityUpdate(now);
          
          // Update courses with waitlist status
          if (userWaitlistEntries.length > 0) {
            setCourses(prevCourses => prevCourses.map(course => {
              const waitlistEntry = userWaitlistEntries.find(w => 
                w.courseId === course.id || w.courseId === course.courseId
              );
              return {
                ...course,
                isOnWaitlist: !!waitlistEntry,
                waitlistStatus: waitlistEntry?.status,
                waitlistEntry: waitlistEntry
              };
            }));
            console.log('Updated courses with waitlist status');
          }
        } catch (err) {
          console.error('Error loading course availability:', err);
        }
      }

      setError(null);
    } catch (err) {
      console.error('Failed to load courses', err);
      setError(t('course.errors.loadFailed'));
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  // Handlers - moved from StudentDashboard
  const handleBookCourse = async (courseId, promotionInfo = null) => {
    try {
      console.log('Booking course:', courseId, 'with promotion info:', promotionInfo);
      setLoadingCourse(courseId);

      // Check if user has a promotion first
      let hasPromotion = false;
      let promotedStudent = null;
      
      // For guardians, check if any of their students have a promotion
      if (isGuardian) {
        try {
          // Use already loaded waitlist entries or reload them
          let currentWaitlistEntries = waitlistEntries;
          if (!currentWaitlistEntries || currentWaitlistEntries.length === 0) {
            const response = await getWaitlistEntries();
            currentWaitlistEntries = response?.entries || response || [];
          }
          console.log('Guardian waitlist entries for booking check:', currentWaitlistEntries);
          
          // Check if any student has a promotion for this course
          const promotedEntry = currentWaitlistEntries.find(entry => 
            entry.courseId === courseId && 
            entry.status === 'promoted' &&
            entry.promotionExpiry && 
            new Date(entry.promotionExpiry) > new Date()
          );
          
          if (promotedEntry) {
            hasPromotion = true;
            promotedStudent = {
              isPromoted: true,
              studentEmail: promotedEntry.studentEmail,
              studentName: promotedEntry.studentName,
              promotionExpiry: promotedEntry.promotionExpiry
            };
            console.log('Found promoted student:', promotedStudent);
            
            // Show which student is being booked
            toast.info(`Booking for promoted student: ${promotedStudent.studentName}`);
          }
        } catch (err) {
          console.log('Error checking guardian student promotions:', err);
        }
      } else {
        // For regular students, check their own promotion
        try {
          const promotionStatus = await checkPromotionStatus(courseId);
          console.log('Promotion status check:', promotionStatus);
          hasPromotion = promotionStatus?.hasValidPromotion || false;
        } catch (err) {
          console.log('No promotion found or error checking:', err);
        }
      }

      // If no promotion, check general availability
      if (!hasPromotion) {
        const availability = await checkAvailability(courseId);
        console.log('Availability check result:', availability);

        if (availability.availableSpots <= 0) {
          // Before offering to join waitlist, check if user is already on waitlist
          if (isGuardian) {
            // Use already loaded waitlist entries or reload them
            let currentWaitlistEntries = waitlistEntries;
            if (!currentWaitlistEntries || currentWaitlistEntries.length === 0) {
              const response = await getWaitlistEntries();
              currentWaitlistEntries = response?.entries || response || [];
            }
            
            const courseWaitlistEntries = currentWaitlistEntries.filter(entry => entry.courseId === courseId);

            // Check if there are promoted students that need to be booked first
            const promotedStudents = courseWaitlistEntries.filter(entry => entry.status === 'promoted');
            if (promotedStudents.length > 0) {
              const studentNames = promotedStudents.map(s => s.studentName).join(', ');
              toast.warning(
                `The following student(s) have been promoted for this course: ${studentNames}. You must complete their booking first before adding other students to the waitlist.`,
                { autoClose: 8000 }
              );
              return;
            }

            // Check if ANY students are already on the waitlist - but don't block adding more
            const waitingStudents = courseWaitlistEntries.filter(entry =>
              entry.status === 'waiting' || entry.status === 'active'
            );

            if (waitingStudents.length > 0) {
              const studentNames = waitingStudents.map(s => s.studentName).join(', ');
              toast.info(`Note: ${studentNames} ${waitingStudents.length === 1 ? 'is' : 'are'} already on the waitlist. You can still add other students.`);
              // Don't return here - allow the guardian to continue and add other students
            }
          }
          
          // Offer to join waitlist
          // Show toast with action instead of confirm dialog
          toast.warning(
            <div>
              <p className="mb-2">{t('course.waitlist.fullMessage')}</p>
              <button
                onClick={() => {
                  toast.dismiss(); // Dismiss this toast
                  handleWaitlist(courseId);
                }}
                className="mr-2 px-3 py-1 bg-yellow-600 text-white rounded hover:bg-yellow-700"
              >
                {t('course.waitlist.joinButton')}
              </button>
              <button
                onClick={() => toast.dismiss()}
                className="px-3 py-1 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              >
                {t('common.cancel')}
              </button>
            </div>,
            {
              autoClose: false,
              closeButton: false
            }
          );
          return;
        }
      }

      // If promoted or spots available, trigger booking wizard in parent
      if (hasPromotion) {
        console.log('User has valid promotion - proceeding with booking');
      }
      if (onShowBookingWizard) {
        // Pass promotion info if this is a promoted booking  
        const promotionData = promotedStudent || (hasPromotion ? { isPromoted: true } : promotionInfo);
        onShowBookingWizard(courseId, null, false, null, promotionData);
      } else {
        console.error('onShowBookingWizard function not provided to CourseList component');
        toast.error(t('course.errors.bookingFailed'));
      }
    } catch (err) {
      console.error('Error checking course availability:', err);

      // Show error toast with action
      toast.error(
        <div>
          <p className="mb-2">{t('course.errors.availabilityCheckFailed')}</p>
          <button
            onClick={() => {
              toast.dismiss();
              onShowBookingWizard(courseId, null);
            }}
            className="mr-2 px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            {t('common.proceed')}
          </button>
          <button
            onClick={() => toast.dismiss()}
            className="px-3 py-1 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
          >
            Cancel
          </button>
        </div>,
        {
          autoClose: false,
          closeButton: false
        }
      );
    } finally {
      setLoadingCourse(null);
    }
  };

  // Update the handleWaitlist function in CourseList.js:
  const handleStudentSelectedForWaitlist = async (student) => {
    if (!selectedCourseForWaitlist) return;
    
    try {
      setLoadingCourse(selectedCourseForWaitlist.id || selectedCourseForWaitlist.courseId);
      
      const waitlistData = {
        courseId: selectedCourseForWaitlist.id || selectedCourseForWaitlist.courseId,
        email: student.email,
        studentName: student.name,
        guardianEmail: user?.email,
        isGuardianBooking: true,
        formData: {
          student_name: student.name,
          parent_email: user?.email,
          date_of_birth: student.dateOfBirth,
          gender: student.gender,
          school: student.school,
          year_level: student.yearLevel
        }
      };
      
      const result = await joinWaitlist(waitlistData);
      
      // Show success toast
      toast.success(`${student.name} has been added to the waitlist for ${selectedCourseForWaitlist.title || selectedCourseForWaitlist.name}`);
      
      // Refresh data
      await loadCourses();
      await loadWaitlistEntries();
      
      // Switch to waitlist tab (optional - could stay on courses)
      // onTabChange('waitlist');
      
      setShowStudentSelectDialog(false);
      setSelectedCourseForWaitlist(null);
    } catch (err) {
      console.error('Failed to add student to waitlist:', err);
      toast.error(err.message || 'Failed to add student to waitlist');
    } finally {
      setLoadingCourse(null);
    }
  };

  const handleWaitlist = async (courseId) => {
    console.log('handleWaitlist called:', {
      courseId,
      isGuardian,
      groups: groups,
      userEmail: user?.email || user?.username
    });
    
    // For guardians, show student selection dialog
    if (isGuardian) {
      console.log('Guardian detected, showing student selection dialog');
      const course = courses.find(c => c.id === courseId || c.courseId === courseId);
      console.log('Selected course for waitlist:', course);
      setSelectedCourseForWaitlist(course);
      setShowStudentSelectDialog(true);
      console.log('Dialog state set:', { showStudentSelectDialog: true });
      return;
    }
    
    console.log('Regular student detected, joining waitlist directly');
    // For regular students, join waitlist directly
    try {
      setLoadingCourse(courseId);
      const result = await joinWaitlist({ courseId });

      // Show success toast
      toast.success(result.message || t('course.waitlist.successMessage'));

      // Optionally refresh courses
      await loadCourses();

      // Switch to waitlist tab
      onTabChange('waitlist');
    } catch (err) {
      // Extract the actual error message
      let errorMessage = t('course.waitlist.errorMessage');

      if (err.response && err.response.data && err.response.data.message) {
        // If error response has a message, use it
        errorMessage = err.response.data.message;
        
        // If the error is about Guardian needing to select a student, show the dialog
        if (errorMessage.includes('Guardians must select a student')) {
          console.log('Backend detected Guardian, showing student selection dialog');
          const course = courses.find(c => c.id === courseId || c.courseId === courseId);
          setSelectedCourseForWaitlist(course);
          setShowStudentSelectDialog(true);
          toast.info('Please select which student to add to the waitlist');
          return;
        }
      } else if (err.message) {
        // If error has a message property, use it
        errorMessage = err.message;
      }

      // Show error toast with the specific message
      toast.error(errorMessage);

      console.error('Waitlist error:', err);
    } finally {
      setLoadingCourse(null);
    }
  };

  const handleViewDetails = (course) => {
    setSelectedCourseDetails(course);
    setShowCourseDetails(true);
  };

  // Helper functions (already in CourseList)
  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };


    // gender specific total spots calculation
  const getGenderSpecificTotal = (course) => {
    console.log('🚻 [DEBUG] getGenderSpecificTotal called with:', {
      courseId: course.id,
      availableSpots: course.availableSpots,
      genderAvailability: course.genderAvailability,
      genderPolicy: course.genderPolicy
    });

    // Check if we have gender availability data
    if (course.genderAvailability) {
      const availableSpots = course.availableSpots;

      // Check if the available spots match female availability
      if (course.genderAvailability.female &&
          course.genderAvailability.female.available === availableSpots) {
        console.log('🚻 [DEBUG] Using female total:', course.genderAvailability.female.total);
        return course.genderAvailability.female.total;
      }

      // Check if the available spots match male availability
      if (course.genderAvailability.male &&
          course.genderAvailability.male.available === availableSpots) {
        console.log('🚻 [DEBUG] Using male total:', course.genderAvailability.male.total);
        return course.genderAvailability.male.total;
      }

      // If no exact match, but we have gender data, use the larger total
      // (assumes mixed policy allows access to the larger pool)
      const femaleTotal = course.genderAvailability.female?.total || 0;
      const maleTotal = course.genderAvailability.male?.total || 0;

      if (femaleTotal > 0 || maleTotal > 0) {
        const maxTotal = Math.max(femaleTotal, maleTotal);
        console.log('🚻 [DEBUG] Using max total:', maxTotal);
        return maxTotal;
      }
    }

    // Final fallback to total spots
    console.log('🚻 [DEBUG] Using fallback totalSpots:', course.totalSpots);
    return course.totalSpots || 0;
  };

  const getAvailabilityStatus = (course) => {
    const availability = course.availability || courseAvailability[course.id];
    
    //console.log('🔍 [DEBUG] getAvailabilityStatus for course:', {
/*       courseId: course.id,
      courseTitle: course.title,
      'course.availability': course.availability,
      'courseAvailability[course.id]': courseAvailability[course.id],
      'final availability': availability,
      'course.availableSpots': course.availableSpots,
      'course.totalSpots': course.totalSpots
    }); */

    if (!availability) {
      const availableSpots = course.availableSpots || 0;
      const totalSpots = course.totalSpots || 0;
      // FIX: Use totalSpots from course data, not gender calculation
      const courseTotal = course.totalSpots || course.maxCapacity || 100;
      
      //console.log('🔍 [DEBUG] Using course fallback data for', course.id, ':', {
/*         availableSpots,
        totalSpots,
        'availableSpots <= 0': availableSpots <= 0,
        'availableSpots <= 3': availableSpots <= 3
      }); */

      if (availableSpots <= 0) {
        const result = {
          status: 'full',
          text: t('course.status.full'),
          color: 'bg-red-100 text-red-800'
        };
        //console.log('🔍 [DEBUG] Course', course.id, 'status (fallback):', result);
        return result;
      }

      if (availableSpots <= 3 && availableSpots > 0 && courseTotal !== 0 && courseTotal >= 0) {
        const result = {
          status: 'limited',
          text: t('course.status.spotsLeft_plural'), // Just show "Some spots left" without number
          color: 'bg-yellow-100 text-yellow-800'
        };
        //console.log('🔍 [DEBUG] Course', course.id, 'status (fallback):', result);
        return result;
      }

      
      const result = {
        status: 'available',
        text: t('course.status.available', { available: availableSpots, total: courseTotal }),
        color: 'bg-green-100 text-green-800'
      };
      //console.log('🔍 [DEBUG] Course', course.id, 'status (fallback):', result);
      return result;
    }

    const { availableSpots, totalSpots } = availability;
    
    //console.log('🔍 [DEBUG] Using availability data for', course.id, ':', {
/*       availableSpots,
      totalSpots,
      'availableSpots <= 0': availableSpots <= 0,
      'availableSpots <= 3': availableSpots <= 3
    }); */

    if (availableSpots <= 0) {
      const result = {
        status: 'full',
        text: t('course.status.full'),
        color: 'bg-red-100 text-red-800'
      };
      //console.log('🔍 [DEBUG] Course', course.id, 'status (availability):', result);
      return result;
    }

    if (availableSpots <= 3 && availableSpots > 0) {
      const result = {
        status: 'limited',
        text: t('course.status.spacesLeft_plural'), // Just show "Some spaces left" without number
        color: 'bg-yellow-100 text-yellow-800'
      };
      //console.log('🔍 [DEBUG] Course', course.id, 'status (availability):', result);
      return result;
    }

    // FIX: Use totalSpots from API response, not gender calculation
    const result = {
      status: 'available',
      text: t('course.status.available', { available: availableSpots, total: totalSpots }),
      color: 'bg-green-100 text-green-800'
    };
    //console.log('🔍 [DEBUG] Course', course.id, 'status (availability):', result);
    return result;
  };

  const formatPrice = (price, currency = 'EUR') => {
    const currencySymbols = {
      EUR: '€',
      USD: '$',
      GBP: '£',
      CAD: 'C$',
      AUD: 'A$'
    };

    const symbol = currencySymbols[currency] || currency;
    const formattedPrice = parseFloat(price || 0).toFixed(2);
    return `${symbol}${formattedPrice}`;
  };

  // Function to refresh all course availability
  const refreshAllAvailability = async () => {
    setIsRefreshingAvailability(true);
    try {
      const availabilityPromises = courses.map(async (course) => {
        try {
          const availability = await checkAvailability(course.id || course.courseId);
          return {
            courseId: course.id || course.courseId,
            availableSpots: availability.availableSpots ?? course.availableSpots ?? 0,
            totalSpots: availability.totalSpots ?? course.totalSpots ?? 0,
            isFullyBooked: (availability.availableSpots ?? course.availableSpots ?? 0) <= 0
          };
        } catch (err) {
          console.error(`Failed to refresh availability for course ${course.id}:`, err);
          return null;
        }
      });

      const availabilities = await Promise.all(availabilityPromises);
      const availabilityMap = {};
      availabilities.forEach(avail => {
        if (avail) {
          availabilityMap[avail.courseId] = avail;
          // Update cache
          availabilityCacheRef.current.set(avail.courseId, {
            data: avail,
            timestamp: Date.now()
          });
        }
      });

      setCourseAvailability(availabilityMap);
      setLastAvailabilityUpdate(Date.now());
      toast.success('Availability refreshed');
    } catch (err) {
      console.error('Error refreshing availability:', err);
      toast.error('Failed to refresh availability');
    } finally {
      setIsRefreshingAvailability(false);
    }
  };

  // Render the full tab content
  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">{t('student.tabs.availableCourses')}</h2>
        <div className="flex items-center space-x-2">
          {lastAvailabilityUpdate && (
            <span className="text-sm text-gray-500">
              Updated {new Date(lastAvailabilityUpdate).toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={refreshAllAvailability}
            disabled={isRefreshingAvailability || loading}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Refresh all availability"
          >
            <RefreshCw className={`w-4 h-4 mr-1.5 ${isRefreshingAvailability ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3">{t('course.loading')}</span>
        </div>
      )}

      {error && (
        <div className="text-center py-8">
          <div className="text-red-600 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={loadCourses}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            {t('common.tryAgain')}
          </button>
        </div>
      )}

      {!loading && !error && (!courses || courses.length === 0) && (
        <div className="text-center py-12">
          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('course.noCourses.title')}</h3>
          <p className="text-gray-600">{t('course.noCourses.message')}</p>
        </div>
      )}

      {!loading && !error && courses && courses.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((course) => {
            const courseId = course.id || course.courseId;
            const availabilityStatus = getAvailabilityStatus(course);
            const isLoading = loadingCourse === courseId;
            const displayPrice = course.basePrice || course.price || 0;
            const currency = course.currency || 'EUR';
            
            // Check if any guardian students are promoted for this course
            const promotedStudentsForCourse = isGuardian ? 
              waitlistEntries.filter(entry => 
                entry.courseId === courseId && 
                entry.status === 'promoted' &&
                entry.promotionExpiry && 
                new Date(entry.promotionExpiry) > new Date()
              ) : [];

            return (
              <div key={courseId} className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
                {course.imageUrl && (
                  <div className="aspect-w-16 aspect-h-9">
                    <img
                      src={course.imageUrl}
                      alt={course.title}
                      className="w-full h-48 object-cover rounded-t-lg"
                    />
                  </div>
                )}

                <div className="p-6">
                  {/* Show promoted student banner if applicable */}
                  {promotedStudentsForCourse.length > 0 && (
                    <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-green-800">
                            {promotedStudentsForCourse.length === 1 
                              ? `${promotedStudentsForCourse[0].studentName} has been promoted!`
                              : `${promotedStudentsForCourse.length} students promoted!`
                            }
                          </p>
                          <p className="text-xs text-green-600 mt-1">
                            Complete booking before {new Date(promotedStudentsForCourse[0].promotionExpiry).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                      {course.title}
                    </h3>
                    <div className="text-right">
                      {(() => {
                        const status = getAvailabilityStatus(course);
                        return (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                            {status.text} 
                          </span>
                        );
                      })()}
                    </div>
                  </div>

                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {course.description || t('course.noDescription')}
                  </p>

                  <div className="space-y-2 mb-4">
                    {course.startDate && (
                      <div className="flex items-center text-sm text-gray-500">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span>
                          {formatDate(course.startDate)}
                          {course.endDate && course.endDate !== course.startDate &&
                            ` - ${formatDate(course.endDate)}`
                          }
                        </span>
                      </div>
                    )}

                    {course.duration && (
                      <div className="flex items-center text-sm text-gray-500">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {course.duration}
                      </div>
                    )}

                    {course.location && (
                      <div className="flex items-center text-sm text-gray-500">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {course.location}
                      </div>
                    )}

                    {course.instructor && (
                      <div className="flex items-center text-sm text-gray-500">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        {course.instructor}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between mb-4">
                    <div className="text-2xl font-bold text-gray-900">
                      {formatPrice(displayPrice, currency)}
                      {course.originalPrice && course.originalPrice > displayPrice && (
                        <span className="text-sm font-normal text-gray-500 line-through ml-2">
                          {formatPrice(course.originalPrice, currency)}
                        </span>
                      )}
                    </div>
                    {course.level && (
                      <span className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                        {course.level}
                      </span>
                    )}
                  </div>

                  <div className="space-y-2">
                    {/* New Gender Availability Indicator */}
                    <GenderAvailabilityIndicator
                      courseId={courseId}
                      showDetails={false}
                      compact={true}
                      className="mb-2"
                      isConfirmedFull={confirmedFullCourses.has(courseId)}
                      onAvailabilityChange={(availability) => {
                        // Update local availability state with new gender data
                        // But don't override if CourseStatusIndicator confirmed it's full
                        if (confirmedFullCourses.has(courseId)) {
                          return; // Don't update if we know it's really full
                        }
                        setCourseAvailability(prev => ({
                          ...prev,
                          [courseId]: availability
                        }));
                      }}
                    />
                    
                    <CourseStatusIndicator 
                      courseId={courseId}
                      isOnWaitlist={course.isOnWaitlist}
                      waitlistStatus={course.waitlistStatus}
                      promotedStudents={promotedStudentsForCourse}
                      isGuardian={isGuardian}
                      onStatusChange={(status) => {
                        // Update course with actual status from API
                        // Only mark as confirmed full if the ENTIRE course is full, not just one gender
                        if (status.status === 'full') {
                          // Mark this course as confirmed full
                          console.log(`Course ${courseId} is completely full`);
                          setConfirmedFullCourses(prev => new Set(prev).add(courseId));
                          
                          // IMPORTANT: Override ANY availability data to show 0
                          setCourseAvailability(prev => ({
                            ...prev,
                            [courseId]: {
                              availableSpots: 0,  // Force to 0, don't merge with existing
                              totalSpots: 0,      // Force to 0  
                              isFullyBooked: true,
                              isAvailable: false
                            }
                          }));
                        } else if (status.status === 'gender_full') {
                          // Gender-specific full - mark as full for THIS user's view
                          // but don't affect other genders' views
                          console.log(`Course ${courseId} is gender_full for current user`);
                          setConfirmedFullCourses(prev => new Set(prev).add(courseId));
                          
                          // IMPORTANT: Override ANY availability data to show 0 for this user
                          setCourseAvailability(prev => ({
                            ...prev,
                            [courseId]: {
                              availableSpots: 0,  // Force to 0, don't merge with existing
                              totalSpots: 0,      // Force to 0
                              isFullyBooked: true,
                              isGenderFull: true,
                              isAvailable: false
                            }
                          }));
                        }
                      }}
                      onBookingAction={(courseId, status) => {
                        if (promotedStudentsForCourse.length > 0) {
                          // Guardian with promoted students must book for them first
                          const promotedStudent = promotedStudentsForCourse[0];
                          handleBookCourse(courseId, {
                            isPromoted: true,
                            studentEmail: promotedStudent.studentEmail,
                            studentName: promotedStudent.studentName,
                            promotionExpiry: promotedStudent.promotionExpiry
                          });
                        } else if (status.status === 'full' || status.status === 'waitlisted') {
                          handleWaitlist(courseId);
                        } else if (status.status === 'promoted' && status.waitlistEntry) {
                          // Pass promotion info for restricted booking
                          handleBookCourse(courseId, {
                            isPromoted: true,
                            studentEmail: status.waitlistEntry.studentEmail,
                            studentName: status.waitlistEntry.studentName
                          });
                        } else {
                          handleBookCourse(courseId);
                        }
                      }}
                    />

                    <button
                      onClick={() => handleViewDetails(course)}
                      className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    >
                      {t('course.viewDetails')}
                    </button>
                  </div>

                  {course.tags && course.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {course.tags.slice(0, 3).map((tag, index) => (
                        <span key={index} className="inline-flex px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                          {tag}
                        </span>
                      ))}
                      {course.tags.length > 3 && (
                        <span className="inline-flex px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                          {t('common.more', { count: course.tags.length - 3 })}
                        </span>
                      )}
                    </div>
                  )}

                  {availabilityStatus.status === 'limited' && (
                    <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-center text-yellow-800 text-sm">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        {t('course.limitedAvailability')}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Course Details Modal */}
      <CourseDetailsModal
        course={selectedCourseDetails}
        isOpen={showCourseDetails}
        onClose={() => setShowCourseDetails(false)}
        onBook={handleBookCourse}
      />
      
      {/* Guardian Student Selection Dialog */}
      <GuardianStudentSelectDialog
        isOpen={showStudentSelectDialog && isGuardian}
        onClose={() => {
          console.log('Closing student select dialog');
          setShowStudentSelectDialog(false);
          setSelectedCourseForWaitlist(null);
          setLoadingCourse(null);
        }}
        onSelectStudent={handleStudentSelectedForWaitlist}
        courseId={selectedCourseForWaitlist?.id || selectedCourseForWaitlist?.courseId}
        courseName={selectedCourseForWaitlist?.title || selectedCourseForWaitlist?.name}
        existingWaitlistStudents={waitlistEntries.filter(e => 
          e.courseId === (selectedCourseForWaitlist?.id || selectedCourseForWaitlist?.courseId)
        )}
        existingBookedStudents={[]} // TODO: Add booked students
      />
    </div>
  );
};

export default CourseList;